import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CalendarClock,
  Package,
  Receipt,
  ShoppingCart,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';

import { errorMessage } from '@/api/client';
import { reportsApi } from '@/api/resources';
import { EmptyState, Loading, Money, PageHeader, StatCard } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDate, firstDayOfMonth, todayApi } from '@/lib/dates';
import { formatGs } from '@/lib/currency';

const PRESETS = [
  { label: 'Hoy', from: () => todayApi(), to: () => todayApi() },
  { label: 'Este mes', from: () => firstDayOfMonth(), to: () => todayApi() },
  {
    label: 'Últimos 30 días',
    from: () => {
      const d = new Date();
      d.setDate(d.getDate() - 29);
      return d.toISOString().slice(0, 10);
    },
    to: () => todayApi(),
  },
  {
    label: 'Este año',
    from: () => `${new Date().getFullYear()}-01-01`,
    to: () => todayApi(),
  },
];

function Bar({ label, value, max, tone = 'bg-primary' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="truncate font-medium">{label}</span>
        <Money value={value} className="shrink-0 text-xs text-muted-foreground" />
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Reporte() {
  const [range, setRange] = useState({ from: firstDayOfMonth(), to: todayApi() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await reportsApi.summary({ date_from: range.from, date_to: range.to }));
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo cargar el reporte'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) return <Loading label="Calculando reporte…" />;
  if (!data) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="No se pudo cargar el reporte"
        action={<Button onClick={load} className="rounded-sm">Reintentar</Button>}
      />
    );
  }

  const maxExpense = Math.max(1, ...data.expenses_by_category.map((c) => c.total));
  const maxDay = Math.max(1, ...data.revenue_by_day.map((d) => d.total));
  const maxProduct = Math.max(1, ...data.top_products.map((p) => p.revenue));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reporte"
        subtitle={`${formatDate(data.date_from)} — ${formatDate(data.date_to)}`}
      />

      {/* Selector de período */}
      <div className="rounded-sm border border-border bg-card p-4">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => {
            const from = preset.from();
            const to = preset.to();
            const active = range.from === from && range.to === to;
            return (
              <Button
                key={preset.label}
                size="sm"
                variant={active ? 'default' : 'outline'}
                className="rounded-sm"
                onClick={() => setRange({ from, to })}
              >
                {preset.label}
              </Button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Desde</Label>
            <Input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              className="rounded-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Hasta</Label>
            <Input
              type="date"
              value={range.to}
              min={range.from}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="rounded-sm"
            />
          </div>
        </div>
      </div>

      {/* Resultado del período */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Facturación"
          value={formatGs(data.revenue)}
          hint={`${data.sales_count} venta(s)`}
          icon={ShoppingCart}
        />
        <StatCard
          label="Ganancia bruta"
          value={formatGs(data.gross_profit)}
          hint="Facturación − costo de mercadería"
          icon={TrendingUp}
          tone={data.gross_profit >= 0 ? 'positive' : 'negative'}
        />
        <StatCard
          label="Gastos"
          value={formatGs(data.expenses_total)}
          hint={`Publicidad: ${formatGs(data.advertising_spend)}`}
          icon={Receipt}
        />
        <StatCard
          label="Ganancia neta"
          value={formatGs(data.net_profit)}
          hint="Ganancia bruta − gastos"
          icon={TrendingUp}
          tone={data.net_profit >= 0 ? 'positive' : 'negative'}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Ticket promedio" value={formatGs(data.average_ticket)} />
        <StatCard
          label="ROI publicidad"
          value={data.advertising_spend ? `${data.roi}%` : '—'}
          hint={data.advertising_spend ? 'Ganancia sobre inversión' : 'Sin gasto en publicidad'}
          tone={data.roi >= 0 ? 'positive' : 'negative'}
        />
        <StatCard
          label="Entradas del período"
          value={formatGs(data.period_income)}
          icon={ArrowUpRight}
          tone="positive"
        />
        <StatCard
          label="Salidas del período"
          value={formatGs(data.period_expense)}
          icon={ArrowDownRight}
          tone="negative"
        />
      </div>

      {/* Estado actual del negocio */}
      <div>
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">
          Estado actual
        </h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard
            label="Saldo en caja"
            value={formatGs(data.cash_balance)}
            icon={Wallet}
            tone={data.cash_balance >= 0 ? 'positive' : 'negative'}
          />
          <StatCard
            label="Valor del inventario"
            value={formatGs(data.inventory_value)}
            hint={`${data.total_products} producto(s)`}
            icon={Package}
          />
          <StatCard
            label="Stock bajo"
            value={data.low_stock_count}
            hint={data.low_stock_count ? 'Requieren reposición' : 'Todo en orden'}
            icon={AlertTriangle}
            tone={data.low_stock_count ? 'warning' : 'default'}
          />
          <StatCard
            label="Clientes"
            value={data.total_customers}
            hint={`${data.pending_events} pendiente(s) en agenda`}
            icon={Users}
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Gastos por categoría */}
        <div className="rounded-sm border border-border bg-card p-4">
          <h3 className="mb-4 font-bold">Gastos por categoría</h3>
          {data.expenses_by_category.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Sin gastos en este período
            </p>
          ) : (
            <div className="space-y-3">
              {data.expenses_by_category.map((c) => (
                <Bar
                  key={c.category}
                  label={c.category}
                  value={c.total}
                  max={maxExpense}
                  tone="bg-red-500"
                />
              ))}
            </div>
          )}
        </div>

        {/* Productos más vendidos */}
        <div className="rounded-sm border border-border bg-card p-4">
          <h3 className="mb-4 font-bold">Productos más vendidos</h3>
          {data.top_products.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Sin ventas en este período
            </p>
          ) : (
            <div className="space-y-3">
              {data.top_products.map((p) => (
                <Bar
                  key={p.product_id}
                  label={`${p.product_name} · ${p.quantity}u`}
                  value={p.revenue}
                  max={maxProduct}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Facturación por día */}
      {data.revenue_by_day.length > 0 && (
        <div className="rounded-sm border border-border bg-card p-4">
          <h3 className="mb-4 font-bold">Facturación por día</h3>
          <div className="flex h-40 items-end gap-1 overflow-x-auto">
            {data.revenue_by_day.map((d) => (
              <div
                key={d.category}
                className="flex min-w-[24px] flex-1 flex-col items-center gap-1"
                title={`${formatDate(d.category)}: ${formatGs(d.total)}`}
              >
                <div
                  className="w-full rounded-t-sm bg-primary transition-all"
                  style={{ height: `${Math.max(4, (d.total / maxDay) * 128)}px` }}
                />
                <span className="text-[9px] text-muted-foreground">
                  {d.category.slice(8)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alertas de stock */}
      {data.low_stock.length > 0 && (
        <div className="rounded-sm border border-amber-300 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
          <h3 className="mb-3 flex items-center gap-2 font-bold text-amber-800 dark:text-amber-300">
            <AlertTriangle size={18} />
            Productos con stock bajo
          </h3>
          <div className="space-y-1">
            {data.low_stock.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate">{p.name}</span>
                <span className="shrink-0 font-bold tabular-nums">
                  {p.stock} / mín. {p.stock_min}
                </span>
              </div>
            ))}
          </div>
          <Button asChild variant="outline" size="sm" className="mt-3 rounded-sm">
            <Link to="/stock">Ir a Stock</Link>
          </Button>
        </div>
      )}

      {/* Últimas ventas */}
      <div className="rounded-sm border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-bold">Últimas ventas</h3>
          <Button asChild variant="ghost" size="sm">
            <Link to="/ventas">Ver todas</Link>
          </Button>
        </div>
        {data.recent_sales.length === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title="Todavía no hay ventas"
            description="Registrá la primera venta desde el módulo Ventas."
            action={
              <Button asChild className="rounded-sm">
                <Link to="/ventas">Nueva venta</Link>
              </Button>
            }
          />
        ) : (
          <div className="divide-y divide-border">
            {data.recent_sales.map((sale) => (
              <div key={sale.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {sale.customer_name || 'Consumidor final'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(sale.date)} · {sale.items.length} ítem(s)
                  </p>
                </div>
                <Money value={sale.total} className="shrink-0 font-bold" />
              </div>
            ))}
          </div>
        )}
      </div>

      {data.scheduled_sales > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-sm border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-sm">
            <CalendarClock size={18} className="text-muted-foreground" />
            Tenés <strong>{data.scheduled_sales}</strong> venta(s) agendada(s) sin confirmar.
          </div>
          <Button asChild size="sm" variant="outline" className="rounded-sm">
            <Link to="/ventas">Revisar</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
